import { createRouter, createWebHistory } from 'vue-router'

import { bootstrapSession } from '@/services/session'
import { useAuthStore } from '@/stores/auth'
import { useMaintenanceStore } from '@/stores/maintenance'
import { useMenuStore } from '@/stores/menu'
import { useUserStore } from '@/stores/user'
import { isStaleChunkError, reloadForStaleChunk } from '@/utils/staleChunk'
import { resolveNavigation } from './guards'
import { routes } from './routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      // The router scrolls with `scrollTo`, which ignores `scroll-margin-top`:
      // without passing it on, the sticky header covers the target heading.
      // getElementById, not querySelector: a hash like `#1st` is no valid selector.
      const target = document.getElementById(decodeURIComponent(to.hash.slice(1)))
      const margin = target ? parseFloat(getComputedStyle(target).scrollMarginTop) || 0 : 0
      return { el: to.hash, top: margin, behavior: 'smooth' }
    }
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  // Rehydrates the profile and the menu when needed; both are required to
  // decide whether this navigation is allowed.
  await bootstrapSession()

  const auth = useAuthStore()
  const menu = useMenuStore()
  const user = useUserStore()
  const maintenance = useMaintenanceStore()

  return resolveNavigation(to, {
    isAuthenticated: auth.isAuthenticated,
    isAllowedPath: (path) => menu.isAllowed(path),
    isAdmin: user.isAdmin,
    isEditor: user.isEditor,
    maintenanceActive: maintenance.isActive,
  })
})

// Without this, a failure inside a guard or a lazy import is only a console
// warning and the user is left staring at the previous page.
router.onError((error, to) => {
  // A view chunk from an older deploy no longer exists: load the new build
  // straight at the page the user was trying to open.
  if (isStaleChunkError(error) && reloadForStaleChunk(to?.fullPath)) return
  console.error('[router] navigation failed', error)
})

// The document title is handled by `useDocumentTitle()` in App.vue, not here:
// it must also react to a language change, which is not a navigation.

export default router
